import React, {useCallback, useEffect, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import QuerySummary from "../components/query-summary";
import SearchResultsGrid from "../components/results-grid";
import ResultsFilter from "../components/results-grid/ResultsFilter";
import {useResizeDetector} from "react-resize-detector";
import { DELETE_MARKER, formatMultipleItemTitle, naturalSort, setPageTitle } from "../lib/utils";
import SubmitSearchButton from "../components/SubmitSearchButton";
import {fetchSearchByDataset} from "../queries/search";
import {toast} from "react-toastify";
import _ from "lodash";
import EvidenceModal from "../components/evidence/EvidenceModal";
import EdgeGraph, { DEFAULT_LABEL_SPACING } from "../components/EdgeGraph";
import Select from "react-select";
import LoadingAnimation from "../components/LoadingAnimation";
import Checkbox from "../components/controls/Checkbox";
import GraphTargetDetails from "../components/GraphTargetDetails";
import {markMatchingText} from "../markdown/utils";
import SearchStreamObserver from "../components/SearchStreamObserver";
import Analytics from "../lib/Analytics";
import MolecularEditorModal from "../components/MolecularEditorModal";

const MAX_SEARCH_ITEMS = 200;

const RE_SALT = /^\[?([A-IK-Z][a-z]?H?(\d*[-+])?)]?$/;
const desalt = s => (s || "").split(".").filter(s => !RE_SALT.test(s)).join(".");
const findSalts = (q, r) => {
  const qs = new Set(q);
  if (qs.size === 0 || r.length === 0) {
    return [];
  }
  return r.filter(e => {
    const smiles = e.entity.smiles;
    return !qs.has(smiles) && desalt(smiles).split(".").some(s => qs.has(s));
  });
};

const SearchResults = ({searchSessionStore, eChainStore, userStore, tutorialStore}) => {

  const [paddingTop, setPaddingTop] = useState(null);
  const [summaryMarginTop, setSummaryMarginTop] = useState(0);
  const [previousOffset, setPreviousOffset] = useState(0);
  const [dsFilteredResults, setDSFilteredResults] = useState(null);
  const [selectedDatasetKeys, setSelectedDatasetKeys] = useState(null);
  const [selectedDatasetsCategory, setSelectedDatasetsCategory] = useState(null);
  const [isDatasetsFilteringBusy, setIsDatasetsFilteringBusy] = useState(false);
  const [includeQueryTerms, setIncludeQueryTerms] = useState(null);
  const [includeSalts, setIncludeSalts] = useState(true);
  const [showEvidence, setShowEvidence] = useState(false);
  const [filteredIDs, setFilteredIDs] = useState(null);
  const [showSimilarity, setShowSimilarity] = useState(true);
  const [showIntermediate, setShowIntermediate] = useState(false);
  const [isGraphAnimating, setIsGraphAnimating] = useState(false);
  const virtualizerRef = useRef(null);
  const [sortBySimilarity, setSortBySimilarity] = useState(false);
  const [graphLabelFilter, setGraphLabelFilter] = useState("");
  const [labelSpacing, setLabelSpacing] = useState(DEFAULT_LABEL_SPACING);
  const [debugCopied, setDebugCopied] = useState(false);
  const [graphLoadKey, setGraphLoadKey] = useState(0); // Incremented to cancel ongoing graph loads
  const [timelineFilterActive, setTimelineFilterActive] = useState(false); // Track if filter was set by timeline
  const [showSubstructureEditor, setShowSubstructureEditor] = useState(false);

  // FIXME may no longer be necessary
  const handleFilterResize = useCallback(() => {
    // Push grid content down below the filter element
    const filter = document.querySelector(".grid-filter");
    if (filter && filter.clientHeight !== paddingTop) {
      setPaddingTop(filter.clientHeight);
    }
  }, [paddingTop]);

  const {ref} = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 1000,
    onResize: handleFilterResize
  });

  const {
    authData,
    awaitingInitialResults,
    retainResultsLayout,
    categories,
    categoryTotals,
    categoryResults,
    categoryResultsLength,
    categoryResultsTotal,
    currentCategory,
    datasets,
    dsConfig,
    dsConfigDatasets,
    entities,
    exportEvidenceChains,
    getEntityById,
    fetchGraphData,
    filterString,
    graphTargetCategory,
    isFilterMatched,
    isFiltering: isFilteringByString,
    isCategoryLoading,
    isLoading,
    params: searchParams,
    queryCategory,
    queryEntities,
    searchIDs: queryIDs,
    activityAvailable,
    activityComputing,
    activityProgress,
    computeActivities,
  } = searchSessionStore;

  const {simType, directConnectionID} = searchParams;
  const dcEntityLabel = directConnectionID ? (entities[directConnectionID]?.label || directConnectionID) : null;

  const clearSelectedDatasets = () => {
    console.debug(`Clear datasets filter`);
    setSelectedDatasetKeys(null);
    setSelectedDatasetsCategory(null);
    setIsDatasetsFilteringBusy(false);
    setDSFilteredResults(null);
  };

  const handleEvidenceClose = () => {
    eChainStore.cancelFetch();
    setShowEvidence(false);
  };

  const handleDatasetsFilterChange = datasets => {
    Analytics.event(Analytics.EVENTS.DATASET_FILTER, {
      datasets: datasets?.map(x => x.id).join(",") || "",
      category: currentCategory,
    });
    const keys = datasets?.map(x => x.id).sort();
    if (!keys?.length) {
      clearSelectedDatasets();
    } else if (selectedDatasetsCategory !== currentCategory
               || !_.isEqual(keys, selectedDatasetKeys)) {
      console.debug(`Datasets filter set ${JSON.stringify(keys)}`);
      setSelectedDatasetKeys(keys);
      setSelectedDatasetsCategory(currentCategory);
    }
    else {
      console.debug("No change to datasets filter")
    }
  };

  useEffect(() => {
    if (selectedDatasetKeys === null) {
      setFilteredIDs(null);
      return () => {};
    }
    let ignore = false;
    setIsDatasetsFilteringBusy(true);
    fetchSearchByDataset(searchParams, selectedDatasetKeys, currentCategory, authData)
      .then(({ids}) => {
        if (!ignore) {
          setFilteredIDs(ids);
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsDatasetsFilteringBusy(false);
        }
      });
    return () => ignore = true;
  }, [selectedDatasetKeys, selectedDatasetsCategory, categoryResults]);

  useEffect(() => {
    if (filteredIDs != null) {
      const includedIDs = new Set(filteredIDs);
      const filtered = categoryResults.filter(x => includedIDs.has(x['id']));
      setDSFilteredResults(filtered);
    }
    else {
      setDSFilteredResults(null);
    }
  }, [filteredIDs, categoryResults]);

  // Reset Query Pane to expanded state when query entities or category changes
  useEffect(() => {
    setSummaryMarginTop(0);
    setPreviousOffset(0);
  }, [queryEntities, currentCategory]);

  const handleFilterInputChange = input => {
    Analytics.event(Analytics.EVENTS.FILTER_CHANGE, { query: input.slice(0, 100) });
    searchSessionStore.filterString = input;
  };

  const handleIncludeQueryTermsChange = show => {
    setIncludeQueryTerms(show);
  };

  const handleIncludeSaltsChange = show => {
    setIncludeSalts(show);
  };

  const handleSubstructureFilter = () => {
    setShowSubstructureEditor(true);
  };

  const handleApplySubstructureFilter = (smarts) => {
    searchSessionStore.setSubstructureFilter(smarts);
  };

  const handleClearSubstructureFilter = () => {
    searchSessionStore.clearSubstructureFilter();
  };

  const handleGridScroll = useCallback(e => {
    const grid = e.target;
    const {scrollTop: scrollOffset} = grid;
    const scrollDirection = previousOffset < scrollOffset ? "forward" : "backward";
    const el = document.querySelector(".query-summary-contents");
    if (el) {
      let delta = scrollOffset - summaryMarginTop;
      const bounds = el.getBoundingClientRect();
      const BOTTOM_MARGIN = 10;
      const bottom = bounds.bottom + BOTTOM_MARGIN;
      if (scrollDirection === "forward") {
        if (bottom > window.innerHeight) {
          delta = Math.min(bottom - window.innerHeight, delta);
        }
        else {
          delta = 0;
        }
      }
      else if (scrollDirection === "backward") {
        delta = scrollOffset - previousOffset;
        if (summaryMarginTop > 0) {
          delta = Math.max(-summaryMarginTop, delta);
        }
        else {
          delta = 0;
        }
      }
      if (delta !== 0) {
        setSummaryMarginTop(summaryMarginTop + delta);
      }
      if (scrollOffset !== previousOffset) {
        setPreviousOffset(scrollOffset);
      }
    }
  }, [summaryMarginTop, previousOffset]);

  const markText = useCallback((text, params = {}) => {
    if (isFilteringByString) {
      //console.log(`Mark ${filterString} within ${text}`);
      return markMatchingText(text, filterString, params);
    }
    return text;
  }, [filterString, isFilteringByString]);
  markText.toString = () => `[mark occurrences of '${filterString}']`;

  // FIXME change this to navigate to the graph instead
  const handleShowEvidence = (targetID, targetCategory) => {
    console.debug(`handleShowEvidence ${JSON.stringify(Array.from(selectedDatasetKeys || []))}`);
    Analytics.event(Analytics.EVENTS.RESULT_CLICK, { result_id: targetID, category: currentCategory });
    Analytics.event(Analytics.EVENTS.ECHAINS, { target_id: targetID });
    eChainStore.getEChains(targetID, selectedDatasetKeys);
    setShowEvidence(true);
  };

  useEffect(() => {
    if (includeQueryTerms !== null && (!queryEntities || (isCategoryLoading && categoryResultsLength === 0))) {
      setIncludeQueryTerms(null);
    }
    if (selectedDatasetsCategory != null && selectedDatasetsCategory !== currentCategory) {
      clearSelectedDatasets()
    }
  }, [includeQueryTerms, queryEntities, isCategoryLoading, categoryResultsLength, selectedDatasetsCategory, currentCategory]);

  const edgeDatasets = Object.entries(dsConfigDatasets).reduce((result, [k, v]) => {
    if (v.edges || k === "bingo") {
      result[k] = v;
    }
    return result;
  }, {});

  const isDSFilterActive = selectedDatasetsCategory === currentCategory && selectedDatasetKeys != null;
  const isFiltering = isFilteringByString || isDSFilterActive;
  const currentCategoryLabel = (categories && categories[currentCategory] && categories[currentCategory].name.toLowerCase()) || "";
  const qe = queryEntities ? [...queryEntities] : [];
  const queryEntitiesOnPage = qe.filter(x => x.category === currentCategory);
  const noResults = categoryResultsTotal === 0;
  const filterClass = 'grid-filter'
  const filterClassName = `${filterClass}${noResults && filterString === '' ? ' hidden' : ''}`;
  const showLoadingLayout = (awaitingInitialResults && !retainResultsLayout) || isDatasetsFilteringBusy;
  const showWaiting = isLoading || isDatasetsFilteringBusy;

  const showIncludeQueryTermsToggler = queryEntitiesOnPage.length > 2 && categoryResultsLength > queryEntitiesOnPage.length;
  const forceShowQueryTermMatches = (queryEntitiesOnPage.length < 3 || categoryResultsLength <= queryEntitiesOnPage.length)
    && queryEntitiesOnPage.length > 0;

  useEffect(() => {
    setIncludeQueryTerms(forceShowQueryTermMatches
                           || (includeQueryTerms === null ? !showIncludeQueryTermsToggler : includeQueryTerms));
  }, [forceShowQueryTermMatches, includeQueryTerms, showIncludeQueryTermsToggler])

  //const salts = new Set(currentCategory !== "compound" ? []
  //    : findSalts(queryEntitiesOnPage.map(e => e.smiles).filter(x => x), categoryResults)
  //        .map(e => e.entity.smiles));
  //const showIncludeSaltsToggler = salts.size > 2 && categoryResultsLength > salts.size;
  const salts = new Set();
  const showIncludeSaltsToggler = false;

  const summaryStyle = summaryMarginTop !== null ? {
    marginTop: `${-summaryMarginTop}px`
  } : {};

  const searchTitle = `Submit top ${MAX_SEARCH_ITEMS} results as a new search`;
  const querySet = new Set(qe.map(x => x.id));
  const categoryResultsSet = new Set(categoryResults.map(x => x.id));
  const onlySearchTermsVisible = querySet.size >= categoryResultsSet.size && [...categoryResultsSet].every((x) => querySet.has(x));
  const displayedResults = isDSFilterActive ? (dsFilteredResults || []) : categoryResults;

  // Calculate actual displayed count accounting for query term filtering
  // This matches the logic in SearchResultsGrid where items are filtered by includeQueryTerms
  const termMatchCount = displayedResults.reduce((acc, x) => acc + (querySet.has(x.id) ? 1 : 0), 0);
  const displayedCount = includeQueryTerms || termMatchCount === 0 ? displayedResults.length : displayedResults.length - termMatchCount;

  const disableSearchButton = isCategoryLoading || onlySearchTermsVisible;
  //console.debug(`Displaying ${displayedResults?.length} (ds filter=${isDSFilterActive}) available=${categoryResults?.length}`);

  // Handle timeline item click to filter by publication title
  const handleTimelineItemClick = useCallback((item) => {
    const entity = item.entity || item;
    const title = entity.title || entity.label || item.id;

    // Set filter to the publication's title
    handleFilterInputChange(title);
    setTimelineFilterActive(true);
  }, []);

  // Handle timeline reset - clear filter if it was set by timeline
  const handleTimelineReset = useCallback(() => {
    if (timelineFilterActive) {
      handleFilterInputChange("");
      setTimelineFilterActive(false);
    }
  }, [timelineFilterActive]);

  const [graphInfo, setGraphInfo] = useState(null);
  const [fetchingGraphData, setFetchingGraphData] = useState(false);
  const [edgeSource, setEdgeSource] = useState(null);
  const [edgeTarget, setEdgeTarget] = useState(null);
  const [edgeAttributes, setEdgeAttributes] = useState({});

  const isGraph = currentCategory === "graph";

  useEffect(() => {
    setGraphInfo(null);
    setEdgeSource(null);
    setEdgeTarget(null);
    setEdgeAttributes({});
  }, [graphTargetCategory]);

  useEffect(() => {
    if (isGraph && !awaitingInitialResults
        && graphTargetCategory != null
        && (graphInfo === null || graphInfo.targetCategory !== graphTargetCategory)) {
      const currentLoadKey = graphLoadKey;
      console.log('[GraphLoad] Starting graph load with key:', currentLoadKey);
      setFetchingGraphData(true);
      setGraphInfo(null);
      fetchGraphData(graphTargetCategory, null, {
        isCanceled: () => {
          const canceled = graphLoadKey !== currentLoadKey;
          if (canceled) {
            console.log('[GraphLoad] Request canceled - current key:', graphLoadKey, 'request key:', currentLoadKey);
          }
          return canceled;
        }
      })
        .then(info => {
          if (graphLoadKey === currentLoadKey) {
            console.log('[GraphLoad] Load complete for key:', currentLoadKey);
            setGraphInfo(info);
          } else {
            console.log('[GraphLoad] Load complete but canceled - ignoring result for key:', currentLoadKey);
          }
        })
        .catch(error => {
          if (graphLoadKey === currentLoadKey) {
            if (error instanceof Response) {
              error = `[${error.status}] ${error.statusText}`;
            }
            console.error("Error fetching graph", error);
            toast.error(`Error fetching graph: ${error}`);
          } else {
            console.log('[GraphLoad] Error in canceled request - ignoring for key:', currentLoadKey);
          }
        })
        .finally(() => {
          if (graphLoadKey === currentLoadKey) {
            setFetchingGraphData(false);
          }
        });
    }
  }, [isGraph, awaitingInitialResults, graphTargetCategory, graphLoadKey]);

  useEffect(() => {
    if (queryEntities && queryEntities.length > 0) {
      const { neighborhood } = searchParams;
      const prefix = dcEntityLabel
        ? `[DC ${dcEntityLabel}] `
        : (neighborhood != null ? `[NBR ${neighborhood}] ` : "");
      setPageTitle(`${prefix}${formatMultipleItemTitle(queryEntities)}`);
    }
    // Don't reset title to "Plex Search" when entities are empty — this is transient during
    // loading, and would clobber the title set from history context before navigation.
  }, [queryEntities, searchParams, dcEntityLabel]);

  useEffect(() => {
    // Tutorial auto-trigger disabled (feature in progress)
    return;
  }, [tutorialStore, userStore, awaitingInitialResults, showWaiting, queryEntities, categoryResults]);

  const onNodeHover = (entity) => {

  };

  const onNodeSelect = (entity) => {

  };

  const onDetailTargetChange = (fromID, toID, attrs) => {
    // Handle single node hover - display EntityView in RHS panel
    if (fromID && !toID && attrs?.isSingleNode) {
      setEdgeSource(getEntityById(fromID));
      setEdgeTarget(null);
      setEdgeAttributes({ isSingleNode: true });
    }
    // Handle edge hover/select - display EdgeDetails in RHS panel
    else if (fromID && toID) {
      setEdgeSource(getEntityById(fromID));
      setEdgeTarget(getEntityById(toID));
      setEdgeAttributes(attrs);
    }
    // Clear selection
    else {
      setEdgeSource(null);
      setEdgeTarget(null);
      setEdgeAttributes(null);
    }
  };

  const categoryOptions = Object.keys(categories)
    .filter(key => categoryTotals.size === 0
                   || (key === queryCategory
                       ? (categoryTotals.get(key) || 0) > queryIDs.length
                       : (categoryTotals.get(key) || 0) > 0))
    .map(key => ({
      label: categories[key].name,
      value: key,
    }))
    .sort((a, b) =>
            naturalSort(a.label, b.label));
  // categoryOptions.insert(0, {label: "All", value: "all"})
  const {
    graph: {
      longest: columns,
      adjacency: adjacencyList = [],
      nodes: nodesData = []
    } = {},
    targetCategory
  } = graphInfo || {};

  // CRITICAL: Do NOT sort nodes - adjacency list indices refer to original node positions!
  // Sorting breaks the adjacency index mapping and creates phantom edges between wrong nodes.
  // The server returns nodes in the correct order for the adjacency list.
  // if (nodesData.length > 0) {
  //   nodesData.sort((a, b) => b[0] - a[0]);  // a[0] and b[0] are rank values
  // }

  const getNodeLabel = (id) => {
    return entities[id]?.label;
  }

  return (
    <div className="page-content">
      <SearchStreamObserver searchSessionStore={searchSessionStore} />
      {showLoadingLayout ? (
        <div className="search-results search-results-loading">
          <div className={"grid-loading"}><LoadingAnimation/></div>
          <QuerySummary queryEntities={qe} style={summaryStyle}/>
        </div>
        ) : (
      <div className={`search-results`}>
        {isGraph
         ? (
            <>
              <div className={"graph-results"}>
                <div className={"graph-controls"}>
                  {/* Left section - regular controls */}
                  <div className={"graph-controls-left"}>
                    <div className={"graph-controls-row"}>
                      <label><span>Show links from {categories[queryCategory].name} to</span>
                        <Select
                          placeholder={"Choose a category"}
                          className={"category-select"}
                          classNamePrefix={"category-select"}
                          defaultValue={graphTargetCategory && graphTargetCategory !== "all"
                                        ? {label: categories[graphTargetCategory].name, value: graphTargetCategory}
                                        : {label: "All", value: "all"}}
                          onChange={option => searchSessionStore.graphTargetCategory = option.value}
                          options={categoryOptions}
                          isSearchable={false}
                          isDisabled={isGraphAnimating}
                        />
                      </label>
                      <input
                        key={"node-filter"}
                        placeholder="find..."
                        onChange={(e) => setGraphLabelFilter(e.target.value)}
                        onKeyDown={(e) => e.key === "Escape" && setGraphLabelFilter("")}
                        type="text"
                        value={graphLabelFilter}
                        className={isFilterMatched ? "" : "out-of-bounds"}
                        title={isFilterMatched ? "" : "Nothing matched"}
                        disabled={isGraphAnimating}
                      />
                      <span
                        key={"node-filter-reset"}
                        className={`input-reset inside${graphLabelFilter !== "" ? "" : " disabled"}`}
                        title="Clear input"
                        onClick={() => setGraphLabelFilter("")}
                        style={graphLabelFilter !== "" ? {visibility: "visible"} : {}}>
                        {DELETE_MARKER}
                      </span>
                    </div>
                    <div className={"graph-controls-row"}>
                      {queryCategory === "compound" && (
                        <>
                          <Checkbox
                            checked={sortBySimilarity}
                            label={"Sort by Similarity"}
                            disabled={simType === "" || isGraphAnimating}
                            onChange={(checked) => { setSortBySimilarity(checked); Analytics.event(Analytics.EVENTS.GRAPH_CONFIG, { setting: "sort-by-similarity", value: checked }); }}
                          />
                          <Checkbox
                            checked={!showSimilarity}
                            label={"Hide Similarity"}
                            disabled={simType === "" || isGraphAnimating}
                            onChange={(checked) => { setShowSimilarity(!checked); Analytics.event(Analytics.EVENTS.GRAPH_CONFIG, { setting: "show-similarity", value: !checked }); }}
                          />
                        </>
                      )}
                      <Checkbox
                        checked={!showIntermediate}
                        label={"Hide Intermediate"}
                        disabled={isGraphAnimating}
                        onChange={(checked) => { setShowIntermediate(!checked); Analytics.event(Analytics.EVENTS.GRAPH_CONFIG, { setting: "show-intermediate", value: !checked }); }}
                      />
                    </div>
                  </div>

                  {/* Right section - admin controls */}
                  {userStore.isAdmin && (
                    <div className={"graph-controls-right"}>
                      <label className={"label-spacing-control"}>
                        <span>Label Spacing: {labelSpacing}</span>
                        <input
                          type="range"
                          min="5"
                          max="50"
                          step="1"
                          value={labelSpacing}
                          onChange={(e) => { setLabelSpacing(Number(e.target.value)); Analytics.event(Analytics.EVENTS.GRAPH_CONFIG, { setting: "label-spacing", value: Number(e.target.value) }); }}
                          disabled={isGraphAnimating}
                        />
                      </label>
                      <button
                        className={`debug-button${debugCopied ? ' copied' : ''}`}
                        onClick={() => {
                          // Copy debug info to clipboard - logic moved from EdgeGraph
                          setDebugCopied(true);
                          setTimeout(() => setDebugCopied(false), 2000);
                        }}
                        title="Copy graph structure to clipboard for debugging"
                        disabled={isGraphAnimating}
                      >
                        {debugCopied ? '✓ Copied!' : 'Debug'}
                      </button>
                    </div>
                  )}
                </div>
                {graphTargetCategory === null
                 ? null
                 : fetchingGraphData
                   ? (<LoadingAnimation network={true} />)
                   : (<div className="graph-view">
                        <EdgeGraph
                          columnCount={columns}
                          nodesData={nodesData}
                          adjacencyList={adjacencyList}
                          queryCategory={queryCategory}
                          targetCategory={targetCategory}
                          queryIDs={queryIDs}
                          datasets={datasets}
                          showSimilarity={showSimilarity}
                          showIntermediate={showIntermediate}
                          sortBySimilarity={sortBySimilarity}
                          onDetailTargetChange={onDetailTargetChange}
                          onAnimationChange={setIsGraphAnimating}
                          labelFilter={graphLabelFilter}
                          getNodeLabel={getNodeLabel}
                          labelSpacing={labelSpacing}
                          isAdmin={userStore.isAdmin}
                        />
                      </div>)}
              </div>
              <GraphTargetDetails
                edgeSource={edgeSource}
                edgeTarget={edgeTarget}
                edgeAttributes={edgeAttributes}
                datasets={datasets} />
            </>
         ) : (
           <>
             <ResultsFilter
               /*ref={ref}*/
               className={filterClassName}
               currentCategory={currentCategory}
               currentCategoryLabel={currentCategoryLabel}
               filterString={filterString}
               onFilterStringChange={handleFilterInputChange}
               onDatasetsFilterChange={onlySearchTermsVisible ? null : handleDatasetsFilterChange}

               onIncludeQueryTermsChange={handleIncludeQueryTermsChange}
               includeQueryTerms={includeQueryTerms}
               showIncludeQueryTermsToggler={showIncludeQueryTermsToggler}

               onIncludeSaltsChange={handleIncludeSaltsChange}
               includeSalts={includeSalts}
               showIncludeSaltsToggler={showIncludeSaltsToggler}

               displayedCount={displayedCount}
               displayedItems={displayedResults}
               totalCount={categoryResultsTotal}
               showResultsFilter={!awaitingInitialResults || retainResultsLayout}
               disabled={(categoryResultsLength === 0 && isCategoryLoading) || isDatasetsFilteringBusy}
               showLoading={isCategoryLoading}
               isFiltering={isFiltering}
               isFilterMatched={isFilterMatched}
               enableDSFilter={queryCategory !== currentCategory}
               isDatasetsFilteringBusy={isDatasetsFilteringBusy}
               filteringDatasetKeys={isDSFilterActive ? selectedDatasetKeys : Object.keys(edgeDatasets)}
               datasets={edgeDatasets}
               searchButton={(
                 <SubmitSearchButton
                   ids={displayedResults.map(e => e['id']).slice(0, MAX_SEARCH_ITEMS)}
                   dsConfig={dsConfig}
                   category={currentCategory}
                   title={searchTitle}
                   label=""
                   disabled={disableSearchButton}
                 />
               )}
               getCategoryResultsClipboardText={() => searchSessionStore.categoryClipboardText }
               isAdmin={userStore.isAdmin}
               onTimelineItemClick={handleTimelineItemClick}
               onTimelineReset={handleTimelineReset}
               onComputeActivities={(onComplete) => searchSessionStore.computeActivities(onComplete)}
               activityAvailable={activityAvailable}
               activityComputing={activityComputing}
               activityProgress={activityProgress}
               onSubstructureFilter={handleSubstructureFilter}
               onClearSubstructureFilter={handleClearSubstructureFilter}
               substructureQuery={searchSessionStore.substructureQuery}
               substructureLoading={searchSessionStore.substructureLoading}
             />
             <MolecularEditorModal
               showEditor={showSubstructureEditor}
               onCloseEditor={() => setShowSubstructureEditor(false)}
               onApplyFilter={handleApplySubstructureFilter}
               forceSubstructureQuery={true}
               mode="filter"
             />
             <QuerySummary queryEntities={qe} style={summaryStyle}/>
             <SearchResultsGrid
               showLoading={isCategoryLoading}
               paddingTop={paddingTop}
               onShowEvidence={handleShowEvidence}
               onScroll={handleGridScroll}
               markText={isFilteringByString && isFilterMatched ? markText : null}
               includeQueryTerms={includeQueryTerms}
               isFiltering={isFilteringByString || isDSFilterActive}
               items={displayedResults}
               virtualizerRef={virtualizerRef}
             />
           </>)}
      </div>
      )}
      <EvidenceModal
        showModal={showEvidence}
        onClose={handleEvidenceClose}
        highlightedDatasets={selectedDatasetKeys}
        isFetching={eChainStore.isFetching}
        evidenceChains={eChainStore.paths}
        targetID={eChainStore.targetID}
        exportFn={exportEvidenceChains}
        datasets={datasets}
      />
    </div>
  );
};

//export default withPropsChecker(inject("searchSessionStore", "eChainStore", "userStore")(observer(SearchResult)));
export default inject("searchSessionStore", "eChainStore", "userStore", "tutorialStore")(observer(SearchResults));
